/* CSV parsing.

   Hand-written rather than pulled in as a dependency, because the requirement is
   small and specific: read a file somebody exported from Excel. That means
   quoted fields, embedded commas, embedded newlines, doubled quotes as an escape,
   CRLF line endings, and a UTF-8 BOM on the front — all of which Excel produces
   and a naive `split(',')` mangles silently.

   Silently is the problem. A parser that loses a column shifts every value one
   place left and the import writes plausible nonsense into the register. */

/** A UTF-8 BOM, which Excel writes and which otherwise becomes part of the first
    header name — so 'Asset tag' stops matching. */
const BOM = '﻿';

export interface CsvTable {
  headers: string[];
  /** One entry per data row, keyed by header. Short rows pad with ''. */
  rows: Record<string, string>[];
  /** Rows whose cell count did not match the header count. */
  malformed: { line: number; cells: number; expected: number }[];
}

/** Splits CSV text into a cell grid. Handles quotes, embedded delimiters and
    newlines, doubled-quote escapes, and CR/LF/CRLF. */
export function parseCsvGrid(text: string, delimiter = ','): string[][] {
  const input = text.startsWith(BOM) ? text.slice(1) : text;
  const grid: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let inQuotes = false;
  let i = 0;

  const endCell = () => {
    row.push(cell);
    cell = '';
  };
  const endRow = () => {
    endCell();
    grid.push(row);
    row = [];
  };

  while (i < input.length) {
    const ch = input[i];

    if (inQuotes) {
      if (ch === '"') {
        // A doubled quote inside a quoted field is a literal quote.
        if (input[i + 1] === '"') {
          cell += '"';
          i += 2;
          continue;
        }
        inQuotes = false;
        i += 1;
        continue;
      }
      cell += ch;
      i += 1;
      continue;
    }

    if (ch === '"') {
      inQuotes = true;
      i += 1;
      continue;
    }
    if (ch === delimiter) {
      endCell();
      i += 1;
      continue;
    }
    if (ch === '\r') {
      // CRLF or a lone CR both end the row.
      endRow();
      i += input[i + 1] === '\n' ? 2 : 1;
      continue;
    }
    if (ch === '\n') {
      endRow();
      i += 1;
      continue;
    }

    cell += ch;
    i += 1;
  }

  // Trailing cell, unless the file ended on a row break.
  if (cell !== '' || row.length > 0) endRow();

  // Drop a single trailing empty row, which a file ending in a newline produces.
  if (grid.length > 0) {
    const last = grid[grid.length - 1];
    if (last.length === 1 && last[0] === '') grid.pop();
  }

  return grid;
}

/** Guesses the delimiter from the header line. Excel writes semicolons in some
    locales, and tab-separated exports are common enough to be worth catching. */
export function sniffDelimiter(text: string): string {
  const firstLine = (text.startsWith(BOM) ? text.slice(1) : text).split(/\r?\n/, 1)[0] ?? '';
  const counts: Record<string, number> = { ',': 0, ';': 0, '\t': 0, '|': 0 };
  let inQuotes = false;
  for (const ch of firstLine) {
    if (ch === '"') inQuotes = !inQuotes;
    else if (!inQuotes && ch in counts) counts[ch] += 1;
  }
  const best = Object.entries(counts).sort((a, b) => b[1] - a[1])[0];
  return best && best[1] > 0 ? best[0] : ',';
}

/** Parses to header-keyed rows. First non-empty row is the header. */
export function parseCsv(text: string, delimiter?: string): CsvTable {
  const delim = delimiter ?? sniffDelimiter(text);
  const grid = parseCsvGrid(text, delim);
  if (grid.length === 0) return { headers: [], rows: [], malformed: [] };

  const headers = grid[0].map((h) => h.trim());
  const rows: Record<string, string>[] = [];
  const malformed: CsvTable['malformed'] = [];

  for (let r = 1; r < grid.length; r += 1) {
    const cells = grid[r];
    // A wholly empty line in the middle of a file is noise, not a row.
    if (cells.every((c) => c.trim() === '')) continue;

    if (cells.length !== headers.length) {
      malformed.push({ line: r + 1, cells: cells.length, expected: headers.length });
      // Still import it — padded or truncated — but the caller can warn. Refusing
      // the whole file because one row has a stray comma is not helpful.
    }

    const row: Record<string, string> = {};
    headers.forEach((h, c) => {
      row[h] = (cells[c] ?? '').trim();
    });
    rows.push(row);
  }

  return { headers, rows, malformed };
}
